import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { carsAPI } from '../services/api';
import './SiteSearch.css';

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const SiteSearch = ({ mode = 'icon' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ models: [], variants: [], parts: [] });
  const debounced = useDebouncedValue(q, 280);
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (debounced.trim().length < 2) {
      setData({ models: [], variants: [], parts: [] });
      return;
    }
    let cancelled = false;
    setLoading(true);
    carsAPI
      .search(debounced.trim())
      .then((res) => {
        if (!cancelled) setData(res.data || { models: [], variants: [], parts: [] });
      })
      .catch(() => {
        if (!cancelled) setData({ models: [], variants: [], parts: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const goModel = useCallback(
    (m) => {
      setOpen(false);
      setQ('');
      navigate(`/models/${m.slug || m.id}`);
    },
    [navigate]
  );

  const goVariant = useCallback(
    (v) => {
      setOpen(false);
      setQ('');
      navigate(`/cars/${v.slug || v.id}`);
    },
    [navigate]
  );

  const goPart = useCallback(
    (p) => {
      setOpen(false);
      setQ('');
      const vs = p.variant_slug;
      if (vs) {
        navigate(`/parts/front?variant=${encodeURIComponent(vs)}&category=${p.kind === 'accessory' ? 'accessories' : 'parts'}`);
      } else {
        navigate('/');
      }
    },
    [navigate]
  );

  const partSubtitle = (p) => {
    if (Array.isArray(p.fits) && p.fits.length > 0) return p.fits.join('، ');
    if (p.model_name) return p.model_name;
    return '';
  };

  const hasResults =
    (data.models && data.models.length > 0) ||
    (data.variants && data.variants.length > 0) ||
    (data.parts && data.parts.length > 0);

  if (mode === 'inline') {
    // (ابقِه مدعوم إذا احتجته لاحقاً)
    return (
      <div className="site-search" ref={wrapRef}>
        <div className="site-search-inner">
          <i className="fas fa-search site-search-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            className="site-search-input"
            placeholder="ابحث عن موديل، سيارة، أو قطعة…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            aria-label="بحث"
            autoComplete="off"
          />
          {loading && <span className="site-search-loading" aria-hidden="true" />}
        </div>
        {open && q.trim().length >= 2 && (
          <div className="site-search-dropdown" role="listbox">
            {!loading && !hasResults && <div className="site-search-empty">لا نتائج</div>}
            {data.models?.map((m) => (
              <button key={`m-${m.id}`} type="button" className="site-search-item" onClick={() => goModel(m)}>
                <span className="site-search-badge site-search-badge--model">موديل</span>
                <span className="site-search-item-text">{m.name}</span>
              </button>
            ))}
            {data.variants?.map((v) => (
              <button key={`v-${v.id}`} type="button" className="site-search-item" onClick={() => goVariant(v)}>
                <span className="site-search-badge site-search-badge--variant">سيارة</span>
                <span className="site-search-item-text">{v.model_name ? `${v.model_name} — ` : ''}{v.name}</span>
              </button>
            ))}
            {data.parts?.map((p) => (
              <button key={`p-${p.kind}-${p.id}`} type="button" className="site-search-item" onClick={() => goPart(p)}>
                <span className="site-search-badge site-search-badge--part">{p.kind === 'accessory' ? 'كمالية' : 'قطعة'}</span>
                <span className="site-search-item-text">{p.name || p.part_number}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Icon mode: زر أيقونة + نافذة بحث
  return (
    <div className="site-search site-search--icon" ref={wrapRef}>
      <button
        type="button"
        className="site-search-trigger"
        aria-label="بحث"
        onClick={() => setOpen(true)}
      >
        <i className="fas fa-search" aria-hidden="true" />
      </button>

      {open && (
        <div className="site-search-modal-backdrop" role="presentation" onClick={() => setOpen(false)}>
          <div className="site-search-modal" role="dialog" aria-label="بحث" onClick={(e) => e.stopPropagation()}>
            <div className="site-search-modal-header">
              <div className="site-search-inner site-search-inner--modal">
                <i className="fas fa-search site-search-icon" aria-hidden="true" />
                <input
                  ref={inputRef}
                  type="search"
                  className="site-search-input"
                  placeholder="ابحث عن موديل، سيارة، أو قطعة…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label="بحث"
                  autoComplete="off"
                />
                {loading && <span className="site-search-loading" aria-hidden="true" />}
              </div>
              <button type="button" className="site-search-close" onClick={() => setOpen(false)} aria-label="إغلاق">
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="site-search-modal-body">
              {q.trim().length < 2 ? (
                <div className="site-search-empty">اكتب حرفين على الأقل</div>
              ) : (
                <div className="site-search-dropdown site-search-dropdown--modal" role="listbox">
                  {!loading && !hasResults && <div className="site-search-empty">لا نتائج</div>}
                  {data.models?.map((m) => (
                    <button key={`m-${m.id}`} type="button" className="site-search-item" onClick={() => goModel(m)}>
                      <span className="site-search-badge site-search-badge--model">موديل</span>
                      <span className="site-search-item-text">{m.name}</span>
                    </button>
                  ))}
                  {data.variants?.map((v) => (
                    <button key={`v-${v.id}`} type="button" className="site-search-item" onClick={() => goVariant(v)}>
                      <span className="site-search-badge site-search-badge--variant">سيارة</span>
                      <span className="site-search-item-text">{v.model_name ? `${v.model_name} — ` : ''}{v.name}</span>
                    </button>
                  ))}
                  {data.parts?.map((p) => (
                    <button key={`p-${p.kind}-${p.id}`} type="button" className="site-search-item" onClick={() => goPart(p)}>
                      <span className="site-search-item-text">
                        {p.name || p.part_number}
                        {partSubtitle(p) ? <span className="site-search-item-sub"> — {partSubtitle(p)}</span> : null}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSearch;
